import { useEffect, useState } from "react";
import { Plus, Trash2, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATS = ["Companies", "Colleges", "Schools", "Managers", "Visitors"];
const blank = { game_id: "", team_name: "", org_name: "", category: "Companies", completion_seconds: 0, location_name: "", played_date: "", photo_url: "", members: "", department: "", designation: "", reports_to: "", year: "", contact_email: "", contact_phone: "" };

export default function LeaderboardAdmin() {
  const [games, setGames] = useState([]);
  const [entries, setEntries] = useState([]);
  const [boards, setBoards] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState(null);
  const [mmss, setMmss] = useState({ m: "", s: "" });
  const [uploading, setUploading] = useState(false);
  const [boardForm, setBoardForm] = useState({ name: "", category: "Companies", max_rows: 10, is_active: true });

  const load = () => {
    api.get("/admin/games").then((r) => setGames(r.data));
    api.get("/admin/entries").then((r) => setEntries(r.data));
    api.get("/admin/leaderboards").then((r) => setBoards(r.data));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = { ...form, completion_seconds: (+mmss.m || 0) * 60 + (+mmss.s || 0) };
    try {
      if (editId) await api.put(`/admin/entries/${editId}`, payload);
      else await api.post("/admin/entries", payload);
      toast.success("Saved"); setOpen(false); setForm(blank); setEditId(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const edit = (e) => { setForm(e); setEditId(e.id); setMmss({ m: Math.floor(e.completion_seconds / 60), s: e.completion_seconds % 60 }); setOpen(true); };
  const del = async (id) => { if (!window.confirm("Delete entry?")) return; await api.delete(`/admin/entries/${id}`); load(); };

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photo_url: `${API}/files/${data.path}` }));
      toast.success("Photo uploaded");
    } catch (e) { toast.error(errMsg(e)); } finally { setUploading(false); }
  };

  const addBoard = async () => {
    try { await api.post("/admin/leaderboards", boardForm); toast.success("Leaderboard created"); setBoardForm({ name: "", category: "Companies", max_rows: 10, is_active: true }); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const delBoard = async (id) => { await api.delete(`/admin/leaderboards/${id}`); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-display font-extrabold text-3xl">Leaderboard</h1><p className="text-slate-400">Record team runs with photos & play location.</p></div>
        <Button data-testid="add-entry-btn" onClick={() => { setForm(blank); setEditId(null); setMmss({ m: "", s: "" }); setOpen(true); }} className="bg-violet-600 hover:bg-violet-500"><Plus className="h-4 w-4 mr-1" /> New entry</Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-2xl border border-white/10 bg-[#131a29] overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-slate-950/50 text-slate-400 text-xs uppercase"><tr>{["Team", "Game", "Cat", "Time", "Location", ""].map((h) => <th key={h} className="text-left px-3 py-3">{h}</th>)}</tr></thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`entry-${e.id}`}>
                  <td className="px-3 py-3"><div className="flex items-center gap-2">{e.photo_url && <img src={e.photo_url} className="h-8 w-8 rounded object-cover" alt="" />}<span>{e.team_name}<div className="text-xs text-slate-500">{e.org_name}</div></span></div></td>
                  <td className="px-3 py-3 text-slate-400">{e.game_name}</td>
                  <td className="px-3 py-3 text-slate-400">{e.category}</td>
                  <td className="px-3 py-3 font-mono text-emerald-400">{Math.floor(e.completion_seconds / 60)}:{String(e.completion_seconds % 60).padStart(2, "0")}</td>
                  <td className="px-3 py-3 text-violet-300">{e.location_name}</td>
                  <td className="px-3 py-3"><div className="flex gap-1"><Button size="sm" variant="secondary" onClick={() => edit(e)}>Edit</Button><Button size="sm" variant="ghost" className="text-red-400" onClick={() => del(e.id)}><Trash2 className="h-3.5 w-3.5" /></Button></div></td>
                </tr>
              ))}
              {entries.length === 0 && <tr><td colSpan={6} className="px-3 py-10 text-center text-slate-500">No entries yet.</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#131a29] p-5">
          <h3 className="font-display font-semibold mb-3">Leaderboards</h3>
          <div className="space-y-2 mb-4">
            {boards.map((b) => (
              <div key={b.id} className="flex items-center justify-between rounded-lg bg-slate-950/40 px-3 py-2 text-sm" data-testid={`board-${b.id}`}>
                <span>{b.name} <span className="text-xs text-slate-500">· {b.category}</span></span>
                <button onClick={() => delBoard(b.id)} className="text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
          <div className="space-y-2 border-t border-white/10 pt-3">
            <Input placeholder="New leaderboard name" value={boardForm.name} onChange={(e) => setBoardForm({ ...boardForm, name: e.target.value })} data-testid="board-name" className="bg-slate-950/50 border-white/10" />
            <Select value={boardForm.category} onValueChange={(v) => setBoardForm({ ...boardForm, category: v })}>
              <SelectTrigger className="bg-slate-950/50 border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={addBoard} data-testid="add-board" variant="secondary" className="w-full"><Plus className="h-4 w-4 mr-1" /> Add leaderboard</Button>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#131a29] border-white/10 max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit" : "New"} entry</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={form.game_id} onValueChange={(v) => setForm({ ...form, game_id: v })}>
              <SelectTrigger data-testid="entry-game" className="bg-slate-950/50 border-white/10"><SelectValue placeholder="Game" /></SelectTrigger>
              <SelectContent>{games.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
            </Select>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Team name" value={form.team_name} onChange={(e) => setForm({ ...form, team_name: e.target.value })} data-testid="entry-team" className="bg-slate-950/50 border-white/10" />
              <Input placeholder={form.category === "Colleges" ? "College name" : form.category === "Schools" ? "School name" : "Company / organisation"} value={form.org_name} onChange={(e) => setForm({ ...form, org_name: e.target.value })} data-testid="entry-org" className="bg-slate-950/50 border-white/10" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="bg-slate-950/50 border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
              <Input type="number" placeholder="Min" value={mmss.m} onChange={(e) => setMmss({ ...mmss, m: e.target.value })} data-testid="entry-min" className="bg-slate-950/50 border-white/10" />
              <Input type="number" placeholder="Sec" value={mmss.s} onChange={(e) => setMmss({ ...mmss, s: e.target.value })} data-testid="entry-sec" className="bg-slate-950/50 border-white/10" />
            </div>
            <Input placeholder="Location where they played" value={form.location_name} onChange={(e) => setForm({ ...form, location_name: e.target.value })} data-testid="entry-location" className="bg-slate-950/50 border-white/10" />
            <Textarea placeholder="Team members (comma separated names)" value={form.members} onChange={(e) => setForm({ ...form, members: e.target.value })} data-testid="entry-members" className="bg-slate-950/50 border-white/10" />
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Contact email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="entry-contact-email" className="bg-slate-950/50 border-white/10" />
              <Input placeholder="Contact phone" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="bg-slate-950/50 border-white/10" />
            </div>
            {form.category === "Companies" && (
              <div className="grid grid-cols-3 gap-3">
                <Input placeholder="Department" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} data-testid="entry-department" className="bg-slate-950/50 border-white/10" />
                <Input placeholder="Designation" value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} className="bg-slate-950/50 border-white/10" />
                <Input placeholder="Reports to (manager)" value={form.reports_to} onChange={(e) => setForm({ ...form, reports_to: e.target.value })} data-testid="entry-reports-to" className="bg-slate-950/50 border-white/10" />
              </div>
            )}
            {form.category === "Colleges" && (
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Department / branch" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} data-testid="entry-department" className="bg-slate-950/50 border-white/10" />
                <Input placeholder="Year (e.g. 2nd year)" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} data-testid="entry-year" className="bg-slate-950/50 border-white/10" />
              </div>
            )}
            {form.category === "Schools" && (
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Class / grade" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} data-testid="entry-year" className="bg-slate-950/50 border-white/10" />
                <Input placeholder="Section" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="bg-slate-950/50 border-white/10" />
              </div>
            )}
            <div className="rounded-lg border border-dashed border-white/15 p-3">
              <div className="text-xs text-slate-400 mb-2 font-medium">Group photo</div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 bg-slate-950/50 cursor-pointer text-sm" data-testid="entry-photo-upload">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload group photo
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
                </label>
                {form.photo_url && <img src={form.photo_url} className="h-14 w-14 rounded object-cover" alt="" />}
              </div>
            </div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-entry" className="bg-violet-600 hover:bg-violet-500">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
